import { type EditorPlugin } from '../../types/aha-editor';
import { type Descendant, type Editor, Element, Text, Transforms } from 'slate';
import produce from 'immer';
import { HtmlSerializer } from '../../serialization/HtmlSerializer';
import { getParentList, spliceFragmentIntoList } from '../ListPlugin/utils';
import AhaEditor from '../../AhaEditor';
import {
  isCommonBlockElement,
  isTableElement,
  isTableRowElement,
} from '../../types/guards';
import TableUtils from '../TablePlugin/TableUtils';
import { spliceFragmentIntoTable } from '../TablePlugin/util/spliceFragmentIntoTable';

// We remove some styles from the pasted HTML to prevent confusion, e.g.
// we don't want to keep the blue color from links so that they don't stay blue
// if unlinked. We also don't want background colors most of the time.
function removeUnwantedTextStyles(fragment: Descendant[]) {
  for (const element of fragment) {
    if (Element.isElement(element)) {
      if (isCommonBlockElement(element)) {
        // Remove paragraph styling.
        delete element.lineHeight;
      }

      removeUnwantedTextStyles(element.children);
    } else if (Text.isText(element)) {
      // Remove the text colors and font.
      delete element.textHighlight;
      delete element.textColor;
      delete element.fontFamily;
      delete element.fontSize;
    }
  }
}

function extractBlocksFromSingleCell(fragment: Descendant[]) {
  if (
    fragment.length === 1 &&
    isTableElement(fragment[0]) &&
    fragment[0].children.length === 1 &&
    isTableRowElement(fragment[0].children[0]) &&
    fragment[0].children[0].children.length === 1
  ) {
    if (Element.isElement(fragment[0].children[0].children[0])) {
      return fragment[0].children[0].children[0].children;
    }
    return fragment[0].children[0].children;
  }
  return fragment;
}

// Paste or drop for HTML.
const PasteHtmlPlugin: EditorPlugin = {
  name: 'Paste html plugin',
  insertData: (editor: Editor, data: DataTransfer, controller) => {
    if (data.types.includes(`application/${AhaEditor.clipboardFormatKey}`)) {
      return false; // Use the built-in fragment support, not HTML.
    }

    for (const item of data.items) {
      if (item.type === 'text/html' && item.kind === 'string') {
        const rawHtml = data.getData('text/html');
        const serializer = new HtmlSerializer(
          editor,
          controller?.serializerOptions ?? {}
        );

        let fragment = serializer.deserialize(rawHtml).children;

        fragment = extractBlocksFromSingleCell(fragment);

        fragment = produce(fragment, (draft) => {
          removeUnwantedTextStyles(draft);
        });

        // If we're in a list, use spliceFragmentIntoList to handle the paste
        if (getParentList(editor, editor.selection?.anchor)) {
          return spliceFragmentIntoList(editor, fragment);
        }

        const table = TableUtils.currentTable(editor);
        const tableSelection = controller?.state.pluginState.tableSelection;
        const fragmentIsTable =
          fragment.length === 1 && isTableElement(fragment[0]);
        if (
          table &&
          tableSelection &&
          fragmentIsTable &&
          spliceFragmentIntoTable(editor, tableSelection, fragment)
        ) {
          // spliceFragmentIntoTable handled the paste, so no need to
          // process further
          return true;
        }

        Transforms.insertFragment(editor, fragment);
        return true;
      }
    }

    return false; // This plugin didn't handle the type of data.
  },
};

export default PasteHtmlPlugin;
